import { useTranslate } from '@tolgee/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type {
  PropertyAddressType,
  PropertyFormType,
} from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  AppRoute,
  ConditionEnums,
  EnergyLabelEnums,
  HeatingGenerationEnums,
  PropertyTypeEnums,
  QualityEnums,
} from '@agentselly/immodossier-shared';
import { AppLink } from '~/components/AppLink';
import {
  LevelProgress,
  type ValuationLevel,
} from '~/components/v3/LevelProgress';
import { useFormRegistration } from '~/context/PropertyEditContext';
import { useAutosaveForm } from '~/hooks/useAutosaveForm';
import { useAppParams } from '~/helper/useAppParams';
import { getEnumValues } from '~/shared/getEnumValues';

interface Props {
  readonly dossierId: string;
  readonly address: PropertyAddressType;
  readonly property: PropertyFormType;
  /** Set to 'final' once the loader reports a completed IAZI valuation (field not exposed yet). */
  readonly valuationLevel?: ValuationLevel;
}

type Values = PropertyFormType & PropertyAddressType;
type Key = keyof Values & string;

const QUALITY_FIELDS = [
  'quality_bathrooms',
  'quality_kitchen',
  'quality_flooring',
  'quality_windows',
  'quality_masonry',
] as const;
const CONDITION_FIELDS = [
  'condition_bathrooms',
  'condition_kitchen',
  'condition_flooring',
  'condition_windows',
  'condition_masonry',
] as const;
// ponytail: backend has no single "Gesamtzustand"; Zustand counts as one detail when any condition_* is set.
const TOTAL_DETAILS = 8;

const num = (v: unknown) => (v === '' ? undefined : Number(v));

export function PropertyFormWidgetV3(props: Props) {
  const { t } = useTranslate();
  const params = useAppParams();
  const defaults = Object.fromEntries(
    Object.entries({ ...props.property, ...props.address }).map(
      ([
        k,
        v,
      ]) => [
        k,
        v ?? '',
      ],
    ),
  );
  const form = useForm<Values>({
    defaultValues: defaults as any,
    resolver: zodResolver(ActionSchema[AppAction.PropertyForm] as any),
  });
  const { saveStatus, triggerManualSave, retrySave } = useAutosaveForm({
    form,
    dossierId: props.dossierId,
    propertyId: (props.property as any)?.id || (props.property as any)?._id,
    action: AppAction.PropertyForm,
    debounceMs: 3000,
    showToastOnFirstSave: true,
    onSaveError: () => toast.error(t('property_form_not_saved')),
  });
  useFormRegistration(triggerManualSave);

  const w = form.watch();
  const filled = (v: unknown) => v !== undefined && v !== null && v !== '';
  const detailCount =
    [
      w.renovationYear,
      w.balconyArea,
      ...QUALITY_FIELDS.map((k) => w[k]),
    ].filter(filled).length +
    (CONDITION_FIELDS.some((k) => filled(w[k])) ? 1 : 0);

  const err = (name: Key) =>
    (form.formState.errors as any)?.[name]?.message as string | undefined;
  const UP = (
    <span className="upg">{t('level_for_detail', 'Für Detailbewertung')}</span>
  );

  const field = (
    name: Key,
    label: string,
    opts: { up?: boolean; unit?: string; number?: boolean; step?: string } = {},
  ) => (
    <div className={`fld${opts.up ? ' up' : ''}`}>
      <label htmlFor={`f-${name}`}>
        {label}
        {opts.unit ? ` (${opts.unit})` : ''}
        {opts.up ? UP : null}
      </label>
      <input
        id={`f-${name}`}
        className="inp num"
        aria-invalid={err(name) ? true : undefined}
        inputMode={opts.number ? 'decimal' : undefined}
        {...(opts.number ? { type: 'number', step: opts.step } : {})}
        {...form.register(name as any, opts.number ? { setValueAs: num } : {})}
      />
      {err(name) ? (
        <p
          className="xs"
          role="alert"
          style={{ color: 'var(--v3-bad)' }}
        >
          {err(name)}
        </p>
      ) : null}
    </div>
  );

  const select = (
    name: Key,
    label: string,
    values: string[],
    labelOf: (v: string) => string,
    up?: boolean,
  ) => (
    <div className={`fld${up ? ' up' : ''}`}>
      <label htmlFor={`f-${name}`}>
        {label}
        {up ? UP : null}
      </label>
      <select
        id={`f-${name}`}
        className="inp"
        data-testid={`select-${name}`}
        {...form.register(name as any)}
      >
        <option value="">
          {t('property_form_please_choose', 'Bitte wählen')}
        </option>
        {values.map((v) => (
          <option
            key={v}
            value={v}
          >
            {labelOf(v)}
          </option>
        ))}
      </select>
    </div>
  );

  const checkbox = (name: Key, label: string) => (
    <label
      className="opt"
      key={name}
    >
      <input
        type="checkbox"
        checked={!!w[name]}
        onChange={(e) =>
          form.setValue(name as any, e.target.checked as any, {
            shouldDirty: true,
          })
        }
      />
      {label}
    </label>
  );

  const conditions = getEnumValues(ConditionEnums as any);
  const qualities = getEnumValues(QualityEnums as any);
  const saving = saveStatus === 'saving';
  const failed = saveStatus === 'error' || saveStatus === 'auth_error';

  return (
    <div className="v3-overview stack">
      <div
        className="head"
        data-od-id="page-head"
      >
        <h1>{t('v3_property_edit_title', 'Objektdaten')}</h1>
        <p className="mu">
          {t(
            'v3_property_edit_intro',
            'Mit den markierten Feldern wird aus der Kurzbewertung eine Detailbewertung in Vorbereitung. Abgeschlossen wird sie automatisch, sobald Ihr Berater die IAZI-Bewertung eingeholt hat.',
          )}
        </p>
      </div>

      <LevelProgress
        filled={detailCount}
        total={TOTAL_DETAILS}
        level={props.valuationLevel}
      />

      <form
        className="stack"
        onSubmit={(e) => e.preventDefault()}
      >
        <section
          className="card lg fsec"
          data-od-id="edit-basic"
          aria-labelledby="h-a"
        >
          <h2 id="h-a">{t('v3_property_edit_basic', 'Eckdaten')}</h2>
          <div className="fgrid two">
            {field('street', t('property_form_property_street_name'))}
            {field(
              'streetNumber',
              t('property_form_property_street_number_name'),
            )}
            {field('zip', t('property_form_property_zip_name'))}
            {field('location', t('property_form_property_location_name'))}
            {select(
              'propertyType',
              t('property_form_property_type_name'),
              getEnumValues(PropertyTypeEnums as any),
              (v) => t(`property_form_property_types_label_${v}`),
            )}
            {field('buildingYear', t('property_form_building_year_name'), {
              number: true,
            })}
            {field(
              'renovationYear',
              t(
                'v3_property_edit_renovation_year',
                'Renovationsjahr (letzte grössere Renovation)',
              ),
              { number: true, up: true },
            )}
            {field('livingArea', t('property_form_living_area_name'), {
              number: true,
              unit: 'm²',
            })}
            {field('numberOfRooms', t('property_form_number_of_rooms_name'), {
              number: true,
              step: '0.5',
            })}
            {field(
              'numberOfBathrooms',
              t('property_form_number_of_bathrooms_name'),
              {
                number: true,
              },
            )}
            {field(
              'balconyArea',
              t(
                'property_form_balcony_area_name',
                'Balkon / Terrasse (Fläche)',
              ),
              { number: true, unit: 'm²', up: true },
            )}
            {select(
              'energyLabel',
              t('property_form_energy_label_name'),
              getEnumValues(EnergyLabelEnums as any),
              (v) => t(`property_form_energy_label_${v}`),
            )}
            {select(
              'heatingGenerationType',
              t('property_form_heating_generation_type_name'),
              getEnumValues(HeatingGenerationEnums as any),
              (v) => t(`property_form_energy_label_${v}`),
            )}
          </div>
          <div className="optg">
            {checkbox('hasLift', t('property_form_has_lift_name'))}
            {checkbox(
              'hasSolarPanels',
              t('property_form_has_solar_panels_name'),
            )}
            {checkbox('hasPool', t('property_form_has_pool_name'))}
          </div>
        </section>

        <section
          className="card lg fsec"
          data-od-id="edit-condition"
          aria-labelledby="h-b"
        >
          <h2 id="h-b">
            {t('v3_property_edit_condition', 'Zustand')}
            {UP}
          </h2>
          <p className="mu sm">
            {t('v3_property_edit_condition_q', 'Wie ist der Zustand?')}
          </p>
          <div className="fgrid two">
            {CONDITION_FIELDS.map((k) =>
              select(
                k,
                t(`property_form_${k}_name`),
                conditions,
                (v) => t(`property_form_value_${v}`),
                true,
              ),
            )}
          </div>
        </section>

        <section
          className="card lg fsec"
          data-od-id="edit-qualities"
          aria-labelledby="h-c"
        >
          <h2 id="h-c">
            {t('v3_property_edit_qualities', 'Ausbauqualität')}
            {UP}
          </h2>
          <div className="fgrid two">
            {QUALITY_FIELDS.map((k) =>
              select(
                k,
                t(`property_form_${k}_name`),
                qualities,
                (v) => t(`property_form_value_${v}`),
                true,
              ),
            )}
          </div>
        </section>

        <div className="fbar">
          <button
            type="button"
            className="btn pri"
            data-testid="manual-save-button"
            disabled={saving}
            onClick={() => triggerManualSave()}
          >
            {t(
              'v3_property_edit_save',
              'Speichern und Bewertung aktualisieren',
            )}
          </button>
          <AppLink
            className="btn"
            to={
              AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION
            }
            params={{ ...params, dossierId: props.dossierId } as any}
          >
            {t('dossier_delete_cancel', 'Abbrechen')}
          </AppLink>
        </div>
        {saveStatus !== 'idle' ? (
          <div
            className={`okbox${failed ? ' bad' : ''}`}
            role="status"
            data-testid={`autosave-${failed ? 'error' : saveStatus}`}
          >
            <span>
              {saving
                ? t(
                    'v3_property_edit_saving',
                    'Speichern und Bewertung neu berechnen …',
                  )
                : failed
                ? t('autosave_error')
                : t(
                    'v3_property_edit_saved',
                    'Gespeichert. Die Bewertung wird neu berechnet.',
                  )}
            </span>
            {failed ? (
              <button
                type="button"
                className="btn"
                data-testid="autosave-retry"
                onClick={retrySave}
              >
                {t('autosave_retry')}
              </button>
            ) : null}
          </div>
        ) : null}
      </form>
    </div>
  );
}
